import {
  Badge,
  Button,
  Flex,
  HStack,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
  useColorMode,
} from '@chakra-ui/react'
import {
  ContentNodeUtils,
  InputValueContentNode,
  InputValueDataTypeConfig,
} from '@helo/cms'
import { Assert, Value, flow, pipe } from '@helo/utils'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Icon } from '../../Icons/Icon'
import { Pill } from '../../Pill/Pill'
import { Tooltip } from '../../Tooltip/Tooltip'
import {
  LoadOptionsForPickListFn,
  getFilterObjectContentNode,
  hasFilterFieldDataChanges,
  updateFilterInputValue,
} from '../FilterEditor/FilterEditor.parts'
import { FilterEditorRow } from '../FilterEditorRow'
import { StringFilterEditor } from '../StringFilterEditor/StringFilterEditor'

/** Date filters use "Range"; number filters use "In Range". Both show from→to values. */
const isRangeChipCondition = (condition?: string) =>
  condition === 'Range' || condition === 'In Range'

export interface FilterChipProps {
  condition?: string
  label: string
  value?: string | string[]
  onRemove?: () => void
  /** When provided, chip popover edits this single field and calls onChange on Apply */
  filterInputValue?: InputValueContentNode
  fieldContentNode?: InputValueContentNode
  availableFilters?: InputValueDataTypeConfig[]
  /** Async load options for "Is Any Of" pick list (distinct values for this field). */
  loadOptionsForPickList?: LoadOptionsForPickListFn
  onChange?: (updatedRoot: InputValueContentNode) => void
  /** When true, the chip's editor popover is open on first render. */
  defaultIsPopoverOpen?: boolean
}

export const FilterChip = ({
  condition,
  label,
  value,
  onRemove,
  filterInputValue,
  fieldContentNode,
  availableFilters = [],
  loadOptionsForPickList,
  onChange,
  defaultIsPopoverOpen,
  ...props
}: FilterChipProps) => {
  const { colorMode } = useColorMode()
  const isDark = colorMode === 'dark'

  const [isActive, setIsActive] = useState(false)
  const [isSelected, setIsSelected] = useState(!!defaultIsPopoverOpen)
  const [latestFieldContentNode, setLatestFieldContentNode] = useState<
    InputValueContentNode | undefined
  >(fieldContentNode)
  const popoverContentRef = useRef<HTMLDivElement>(null)

  // Close on click outside, but not when clicking inside react-select menus
  // or date calendar (they render in a portal outside the popover).
  // Two-phase approach: capture phase detects portal clicks while the DOM is
  // intact (before React unmounts portalled menus), bubble phase uses that flag.
  const isPortalClickRef = useRef(false)
  useEffect(() => {
    if (!isSelected) return
    const portalSelector =
      '[class*="react-select"], [id*="react-select"], [data-popover-card], [data-pick-list-filter]'

    const onMouseDownCapture = (e: MouseEvent) => {
      const el = e.target as Element
      isPortalClickRef.current = !!el.closest?.(portalSelector)
    }

    const onMouseDown = (e: MouseEvent) => {
      if (isPortalClickRef.current) {
        isPortalClickRef.current = false
        return
      }
      const target = e.target as Node
      if (popoverContentRef.current?.contains(target)) return
      const el = e.target as Element
      if (el.closest?.('[data-sort-editor-trigger]')) {
        setIsSelected(false)
        return
      }
      setIsSelected(false)
    }

    document.addEventListener('mousedown', onMouseDownCapture, true)
    document.addEventListener('mousedown', onMouseDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDownCapture, true)
      document.removeEventListener('mousedown', onMouseDown)
    }
  }, [isSelected])
  const canEdit =
    !!filterInputValue &&
    !!fieldContentNode &&
    availableFilters.length > 0 &&
    !!onChange

  const hasApplyChanges = useMemo(() => {
    if (!latestFieldContentNode || !fieldContentNode) return false
    return hasFilterFieldDataChanges(
      latestFieldContentNode.data,
      fieldContentNode.data,
    )
  }, [latestFieldContentNode, fieldContentNode])

  const handleApply = () => {
    if (!canEdit || !latestFieldContentNode) {
      setIsSelected(false)
      return
    }

    const updatedInputValue = updateFilterInputValue(
      latestFieldContentNode,
      filterInputValue,
    )

    onChange(updatedInputValue)

    setIsSelected(false)
  }

  const handleClear = () => {
    if (!canEdit || !filterInputValue || !fieldContentNode) {
      setIsSelected(false)
      return
    }
    const filterObjectContentNode = getFilterObjectContentNode(filterInputValue)
    const mergedData = {
      ...(filterObjectContentNode.data as Record<string, unknown>),
    }
    delete mergedData[fieldContentNode.key]
    const updatedFilterObject = ContentNodeUtils.updateContentNode(mergedData)(
      filterObjectContentNode,
    )
    const updatedRoot =
      ContentNodeUtils.updateChildContentNode(updatedFilterObject)(
        filterInputValue,
      )

    onChange?.(updatedRoot)
    setIsSelected(false)
  }

  const handleCancel = () => setIsSelected(false)

  const showChipTooltip =
    !isSelected &&
    Array.isArray(value) &&
    value.length > 0 &&
    !isRangeChipCondition(condition)

  const chipBadge = (
    <Badge
      bg={
        isDark
          ? 'transparent'
          : isSelected
            ? 'presets.turquoise.20'
            : isActive
              ? 'presets.turquoise.20'
              : 'presets.turquoise.10'
      }
      _dark={isDark ? { bg: 'transparent' } : undefined}
      padding="8px"
      cursor="pointer"
      onMouseDown={() => setIsActive(true)}
      onMouseUp={() => setIsActive(false)}
      onMouseLeave={() => setIsActive(false)}
      onClick={() => setIsSelected(!isSelected)}
      tabIndex={0}
      height="32px"
      borderWidth="1px"
      borderStyle="solid"
      borderColor={isDark ? 'presets.overlay.turquoise40' : 'transparent'}
      _hover={{
        borderColor: isDark
          ? 'presets.overlay.turquoise40'
          : 'presets.turquoise.40',
      }}
      {...props}
    >
      <Flex width="100%" align="center" gap="8px">
        <HStack
          flex="1"
          minWidth={0}
          fontSize="12px"
          textColor={isDark ? 'presets.neutral.0' : 'presets.neutral.100'}
          whiteSpace="pre-wrap"
          gap="8px"
        >
          <Pill variant="turquoise" size="sm" py="0px">
            {label}
          </Pill>

          {condition && (
            <Text
              fontSize="12px"
              fontWeight="400"
              color={isDark ? 'presets.neutral.0' : 'presets.neutral.100'}
            >
              {condition}
            </Text>
          )}

          {((value != null && value !== '' && !Array.isArray(value)) ||
            (Array.isArray(value) && value.length > 0)) && (
            <Flex alignItems="center" gap="4px">
              {value != null && value !== '' && !Array.isArray(value) && (
                <Text
                  fontSize="12px"
                  fontWeight="600"
                  color={isDark ? 'presets.neutral.0' : 'presets.neutral.90'}
                >
                  {value}
                </Text>
              )}
              {value &&
                Array.isArray(value) &&
                value.length > 0 &&
                (isRangeChipCondition(condition) && value.length === 2 ? (
                  <Flex alignItems="center" gap="4px">
                    <Text
                      fontSize="12px"
                      fontWeight="600"
                      color={
                        isDark ? 'presets.neutral.0' : 'presets.neutral.90'
                      }
                    >
                      {value[0]}
                    </Text>
                    <Icon
                      icon="ArrowLeftIcon"
                      boxSize="8px"
                      transform="rotate(180deg)"
                      color={
                        isDark ? 'presets.neutral.0' : 'presets.neutral.60'
                      }
                    />
                    <Text
                      fontSize="12px"
                      fontWeight="600"
                      color={
                        isDark ? 'presets.neutral.0' : 'presets.neutral.90'
                      }
                    >
                      {value[1]}
                    </Text>
                  </Flex>
                ) : (
                  <Text
                    fontSize="12px"
                    fontWeight="600"
                    color={isDark ? 'presets.neutral.0' : 'presets.neutral.90'}
                  >
                    {value.length === 1 ? value[0] : `${value.length} Selected`}
                  </Text>
                ))}
            </Flex>
          )}
        </HStack>
        {onRemove && (
          <IconButton
            data-testid="filter-chip-remove-button"
            onClick={e => {
              e.stopPropagation()
              onRemove()
            }}
            aria-label="remove filter"
            borderRadius="4px"
            borderTopLeftRadius="0px"
            borderBottomLeftRadius="0px"
            padding="4px"
            marginY="-7px"
            marginRight="-8px"
            variant="ghost"
            flexShrink={0}
            boxSize="24px"
            minWidth="32px"
            minHeight="30px"
            color={isDark ? 'presets.neutral.0' : 'presets.neutral.90'}
            _hover={{
              bg: isDark
                ? 'presets.overlay.turquoise30'
                : 'presets.turquoise.20',
              color: isDark ? 'presets.neutral.0' : 'presets.turquoise.60',
            }}
            _active={{
              bg: isDark
                ? 'presets.overlay.turquoise40'
                : 'presets.turquoise.30',
            }}
            icon={<Icon icon="XIcon" height="8px" width="8px" />}
          />
        )}
      </Flex>
    </Badge>
  )

  const filterChipContent = showChipTooltip ? (
    <Tooltip
      label={
        <VStack gap="2px" alignItems="start">
          <Text>{label}:</Text>
          <Flex gap="4px" alignItems="center" flexWrap="wrap">
            {(value as string[]).map((item, index) => (
              <Flex key={index} gap="4px" alignItems="center">
                <Text fontWeight="700">{item}</Text>
                {index < (value as string[]).length - 1 && <Text>|</Text>}
              </Flex>
            ))}
          </Flex>
        </VStack>
      }
      placement="bottom"
    >
      {chipBadge}
    </Tooltip>
  ) : (
    chipBadge
  )

  return (
    <Popover
      isOpen={isSelected}
      onOpen={() => {
        setLatestFieldContentNode(fieldContentNode)
        setIsSelected(true)
      }}
      onClose={() => setIsSelected(false)}
      placement="bottom-start"
      closeOnBlur={false}
      isLazy
      lazyBehavior="unmount"
    >
      <PopoverTrigger>{filterChipContent}</PopoverTrigger>
      <Portal>
        <PopoverContent
          ref={popoverContentRef}
          borderRadius="4px"
          boxShadow={
            isDark
              ? '0 4px 24px 0 rgba(0, 0, 0, 0.24)'
              : '0px 6px 20px 0px rgba(0, 0, 0, 0.08), 0px 8px 30px 0px rgba(0, 0, 0, 0.10)'
          }
          minWidth="826px"
          bg={isDark ? 'presets.dark.surfacePanel' : undefined}
          border={isDark ? '1px solid' : undefined}
          borderColor={isDark ? 'presets.neutral.90' : undefined}
        >
          <PopoverBody
            padding="16px"
            bg={isDark ? 'presets.dark.surfacePanel' : undefined}
            color={isDark ? 'presets.neutral.0' : undefined}
          >
            {canEdit && latestFieldContentNode ? (
              <FilterEditorRow
                inputValueContentNode={latestFieldContentNode}
                filters={availableFilters}
                onUpdate={setLatestFieldContentNode}
                showRemoveButton={false}
                disableFieldSelect
                loadOptionsForPickList={loadOptionsForPickList}
                isFilterEditorOpen={isSelected}
              />
            ) : (
              <StringFilterEditor
                inputValueContentNode={
                  {
                    data: value,
                    getDataTypeConfig: () =>
                      ({
                        state: {},
                      }) as InputValueDataTypeConfig,
                  } as InputValueContentNode
                }
                conditionOptions={[]}
              />
            )}
          </PopoverBody>
          <PopoverFooter
            padding="16px"
            bg={isDark ? 'presets.dark.surfacePanel' : undefined}
            color={isDark ? 'presets.neutral.0' : undefined}
            borderTop={isDark ? '1px solid' : undefined}
            borderColor={isDark ? 'presets.neutral.90' : undefined}
          >
            <HStack gap="8px" justifyContent="space-between">
              <Button
                size="sm"
                variant="tertiary"
                onClick={canEdit ? handleClear : handleCancel}
              >
                Clear
              </Button>
              <HStack gap="8px" justifyContent="flex-end">
                <Button size="sm" variant="tertiary" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={canEdit ? handleApply : handleCancel}
                  isDisabled={canEdit && !hasApplyChanges}
                >
                  Apply
                </Button>
              </HStack>
            </HStack>
          </PopoverFooter>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}
